import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useGetQuizUserInfo } from '@/api/gen/quiz-user/quiz-user.gen';
import { PLAIN_FRAME_BODY_CLASS_NAME, ProductContentFrame } from '@/shared/components/ProductContentFrame';
import { SectionHeader } from '@/shared/components/SectionHeader';
import { PathParentLinkButton } from '@/shared/modules/navigation-path';
import { ListLayoutContainer, ListLayoutWrapper } from '@/shared/quizList/Layout';
import { SearchInputQuizForm } from '@/shared/quizList/SearchField';
import { SearchMetaBar } from '@/shared/quizList/SearchField/SearchMetaBar';
import { useProfileSidebarMenu } from '../shared/hooks/useProfileSidebarMenu';
import { UserFolderList } from './components/UserFolderList';
import { useUserFolderList } from './hooks/useUserFolderList';

/**
 * 남의 프로필 공개 폴더 전체 페이지. 프로필 폴더 섹션의 `N개 ›` 로 들어온다.
 * 위에 프로필로 돌아가는 목록 버튼, 그 아래 검색바와 무한 스크롤 목록이다(내 폴더 전체 페이지와 같은 구성).
 */
export const UserFolderListPageView = ({
  userHashId
}: {
  userHashId: string;
}) => {
  const {
    t
  } = useTranslation(['common']);
  const {
    query
  } = useRouter();
  const {
    data: user
  } = useGetQuizUserInfo(userHashId);
  const {
    totalCount,
    isLoading
  } = useUserFolderList(userHashId);
  useProfileSidebarMenu(userHashId, user?.data?.username, `/profile/${userHashId}/folders`);
  if (!user?.data) return null;

  // 길 없이 들어왔으면(새 탭·공유 링크) 프로필로 간다. 그때 검색·정렬은 이어진다 — 두 화면이 같은 쿼리를 읽는다.
  const {
    userHashId: _omit,
    ...listQuery
  } = query;
  return (
    // 목록 버튼은 걸어온 길의 부모 칸으로, 길이 없으면 fallback(그 프로필)으로 간다.
    <ProductContentFrame ready={!isLoading} hasBanner={false} parentLink={<PathParentLinkButton placement="plain"
    // 라벨(「○○님 프로필」)은 목록 버튼이 kind·name 으로 만든다.
    fallback={{
      kind: 'profile',
      href: {
        pathname: `/profile/${userHashId}`,
        query: listQuery
      },
      name: user.data.username
    }} />} data-sentry-element="ProductContentFrame" data-sentry-component="UserFolderListPageView" data-sentry-source-file="UserFolderListPageView.tsx">
      <ListLayoutWrapper className={PLAIN_FRAME_BODY_CLASS_NAME} data-sentry-element="ListLayoutWrapper" data-sentry-source-file="UserFolderListPageView.tsx">
        <ListLayoutContainer className={'max-w-none gap-none max-tablet-sm:gap-none'} data-sentry-element="ListLayoutContainer" data-sentry-source-file="UserFolderListPageView.tsx">
          <div className={'flex w-full flex-col gap-xs'}>
            <SectionHeader title={t('common:folder.seo.ownerFolder', {
              ownerName: user.data.username
            })} count={t('common:formatNumber.count', {
              n: totalCount.toLocaleString()
            })} data-sentry-element="SectionHeader" data-sentry-source-file="UserFolderListPageView.tsx" />
            <div className={'flex items-center justify-between gap-xs pb-xs'}>
              <SearchInputQuizForm eventType={'user'} placeholder={t('common:public.search.placeholder')} data-sentry-element="SearchInputQuizForm" data-sentry-source-file="UserFolderListPageView.tsx" />
              <SearchMetaBar eventType={'user'} data-sentry-element="SearchMetaBar" data-sentry-source-file="UserFolderListPageView.tsx" />
            </div>
          </div>
          <div className={'w-full'}>
            <UserFolderList userHashId={userHashId} data-sentry-element="UserFolderList" data-sentry-source-file="UserFolderListPageView.tsx" />
          </div>
        </ListLayoutContainer>
      </ListLayoutWrapper>
    </ProductContentFrame>
  );
};